import React, { useEffect, useState } from 'react';
import { Card, Col, Dropdown, Modal, Row, Spinner, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../../common/Flex';
import CardDropdown from '../../../common/CardDropdown';
import PaginationEllipsis from '../../../common/PaginationEllipsis';
import SearchAccordion from '../match-schedule/SearchAccordion';
import { _exportPDF } from '../../../../lib/api/api';
import { itemMatch, listMatches } from '../../../../lib/api/match';
import EditMatch from '../match-schedule/EditMatch';
import MatchScoreboard from '../MatchScoreboard';
import { useAppContext } from '../../../../providers/AppProvider';

const formatMatchTime = value => (value ? String(value).slice(0, 5) : '');

const hasReport = item =>
  item.match_reports_id !== null && item.match_reports_id !== undefined && item.match_reports_id !== '';

const PlayedMatches = () => {
  const { userApp } = useAppContext();
  const [search, setSearch] = useState({});
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewLoadingGuid, setViewLoadingGuid] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  useEffect(() => {
    fetchData();
  }, [page, pageSize, search]);

  const fetchData = async (showLoader = true) => {
    if (showLoader) setLoading(true);

    try {
      const response = await listMatches({
        page,
        limit: pageSize,
        ...search,
        is_migrated: 1
      });
      const total = Number(response?.total || 0);
      const limit = Number(response?.limit || pageSize || 100);

      setItems(response?.items || []);
      setTotalPages(Math.max(1, Math.ceil(total / limit)));
    } catch (error) {
      console.error('Greska prilikom ucitavanja odigranih utakmica:', error);
      setItems([]);
      setTotalPages(0);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const handlePageChange = newPage => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  const handleSearch = data => {
    setPage(1);
    setSearch(data);
  };

  const handleView = async matchesGuid => {
    setViewLoadingGuid(matchesGuid);
    try {
      const response = await itemMatch({ matches_guid: matchesGuid });
      setSelectedMatch(Array.isArray(response) ? response[0] : response);
      setShowViewModal(true);
    } catch (error) {
      console.error('Greska prilikom ucitavanja utakmice:', error);
    } finally {
      setViewLoadingGuid(null);
    }
  };

  const handlePrintReport = async matchesGuid => {
    try {
      await _exportPDF(`report/pdf-match-report/${matchesGuid}`);
    } catch (error) {
      console.error('Greska prilikom stampe izvjestaja:', error);
      toast.error(error?.body?.message || error?.message || 'Doslo je do greske prilikom pripreme stampe izvjestaja.');
    }
  };

  const handlePrintCompensation = async matchesGuid => {
    try {
      await _exportPDF(`report/pdf-match-compensation/${matchesGuid}`, undefined, false, 'GET');
    } catch (error) {
      console.error('Greška prilikom štampe uplatnice:', error);
      toast.error(error?.body?.message || error?.message || 'Došlo je do greške prilikom pripreme štampe uplatnice.');
    }
  };

  const canPrintReport = item => {
    const role = userApp?.role_type_id;
    const hasReport = Boolean(item.match_reports_id);
    const hasUserEntity = userApp?.operator_entities_id !== null &&
      userApp?.operator_entities_id !== undefined && userApp?.operator_entities_id !== '';
    const isFirstJudge = hasUserEntity && item.judge_entities_id_1 !== null &&
      item.judge_entities_id_1 !== undefined && item.judge_entities_id_1 !== '' &&
      Number(item.judge_entities_id_1) === Number(userApp.operator_entities_id);
    const hasNoSupervisor = item.supervisor_entities_id === null ||
      item.supervisor_entities_id === undefined ||
      String(item.supervisor_entities_id).trim() === '';
    const allowed = role !== 'clubs' && hasReport &&
      (role !== 'judge' || (hasUserEntity && isFirstJudge && hasNoSupervisor));

    console.log('[canPrintReport]', {
      match_id: item.match_id,
      role,
      match_reports_id: item.match_reports_id,
      user_operator_entities_id: userApp?.operator_entities_id,
      user_operator_entities_id_type: typeof userApp?.operator_entities_id,
      judge_entities_id_1: item.judge_entities_id_1,
      judge_entities_id_1_type: typeof item.judge_entities_id_1,
      supervisor_entities_id: item.supervisor_entities_id,
      hasReport,
      hasUserEntity,
      isFirstJudge,
      hasNoSupervisor,
      allowed
    });

    return allowed;
  };

  const renderActions = item => (
    <CardDropdown>
      <div className="py-2">
        <Dropdown.Item
          disabled={viewLoadingGuid === item.matches_guid}
          onClick={() => handleView(item.matches_guid)}
        >
          Pregled podataka
        </Dropdown.Item>
        {canPrintReport(item) ? (
          <>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => handlePrintReport(item.matches_guid)}>
              Štampa izvještaja
            </Dropdown.Item>
          </>
        ) : null}
        {['admin', 'operator'].includes(userApp?.role_type_id) && Number(item.is_migrated) === 1 && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => handlePrintCompensation(item.matches_guid)}>
              Štampa uplatnice
            </Dropdown.Item>
          </>
        )}
      </div>
    </CardDropdown>
  );

  return (
    <>
      <Row className="g-3">
        <SearchAccordion
          handleSearch={handleSearch}
          showClubFilters={userApp?.role_type_id !== 'clubs'}
          showActiveFilter={false}
        />
        <Col xs={12}>
          <Card className={classNames('h-100')}>
            <Card.Header>
              <Flex>
                <h5 className="mb-0 hover-actions-trigger text-truncate text-nowrap">
                  Odigrane utakmice
                </h5>
              </Flex>
            </Card.Header>
            <Card.Body className="bg-body-tertiary">
              {loading ? (
                <div className="text-center"><Spinner animation="border" role="status" /></div>
              ) : (
                <Row className="mb-3">
                  <div className="d-none d-xl-block">
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>ID</th><th>Takmičenja</th><th className="text-center">Datum</th><th>Parovi / Rezultat</th>
                          <th className="text-center" title="Izvještaj" />
                          <th className="text-end">Akcije</th>
                        </tr>
                      </thead>
                      <tbody className="fs-10">
                        {items.map(item => (
                          <tr key={item.matches_guid || item.match_id}>
                            <td><div className="ms-2">{item.match_id}</div></td>
                            <td><div className="ms-2"><div>{item.competitions_name}</div><div className="text-body-tertiary">{item.round_type_name}</div></div></td>
                            <td className="text-center align-middle"><div><div>{item.date ? format(parseISO(item.date), 'dd.MM.yy') : ''}</div><div className="text-body-tertiary">{formatMatchTime(item.match_time)}</div></div></td>
                            <td><MatchScoreboard item={item} /></td>
                            <td className="text-center align-middle">
                              {hasReport(item) && (
                                <FontAwesomeIcon icon="check-circle" className="text-success fs-8" title="Izvještaj postoji" />
                              )}
                            </td>
                            <td className="text-end">{renderActions(item)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <div className="d-xl-none">
                    <Row className="g-3">
                      {items.map(item => (
                        <Col xs={12} key={item.matches_guid || item.match_id}>
                          <div className="border rounded-4 bg-white h-100 p-3 shadow-sm" style={{ borderColor: '#dfe3ee' }}>
                            <div className="d-flex align-items-start justify-content-between gap-3">
                              <div className="flex-grow-1" style={{ minWidth: 0 }}><div className="fw-semibold text-dark fs-9">{item.competitions_name || '-'}</div></div>
                              <div className="flex-shrink-0">{renderActions(item)}</div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              <span className="badge rounded-pill badge-subtle-primary">#{item.match_id || '-'}</span>
                              <span className="badge rounded-pill badge-subtle-success">{item.round_type_name || '-'}</span>
                              {hasReport(item) && (
                                <span className="badge rounded-pill badge-subtle-success">
                                  <FontAwesomeIcon icon="check-circle" className="me-1" />
                                  Izvještaj
                                </span>
                              )}
                            </div>
                            <div className="text-600 fs-10 mt-2">{item.date ? format(parseISO(item.date), 'dd.MM.yy') : '-'}{item.match_time ? ` u ${formatMatchTime(item.match_time)}` : ''}</div>
                            <div className="mt-2"><MatchScoreboard item={item} /></div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Row>
              )}
            </Card.Body>
            <Card.Footer><Row className="justify-content-center"><PaginationEllipsis page={page} totalPages={totalPages} onPageChange={handlePageChange} /></Row></Card.Footer>
          </Card>
        </Col>
      </Row>
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Pregled podataka utakmice</Modal.Title></Modal.Header>
        <Modal.Body>{selectedMatch && <EditMatch initialData={selectedMatch} onClose={() => setShowViewModal(false)} readOnly />}</Modal.Body>
      </Modal>
    </>
  );
};

export default PlayedMatches;
